import { useState } from "react";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Reveal } from "./Reveal";
import { PROJECTS } from "../lib/data";
import { PhoneFrame, PROJECT_SCREENS } from "./ProjectMockups";

export const Projects = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="projects" className="relative py-20 sm:py-28 lg:py-32" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-laser">
            // Featured Work
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 mb-4">
            Our Work
          </h2>
          <p className="text-slate-400 text-base leading-relaxed">
            A selection of digital products developed around real business requirements.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-16">
          {PROJECTS.map((project, i) => {
            const Screen = PROJECT_SCREENS[project.mockup];
            return (
              <Reveal key={project.id} delay={i * 0.12}>
                <div
                  data-testid={`project-card-${project.id}`}
                  className="group rounded-2xl border border-border-subtle bg-card-dark/70 p-6 sm:p-7 hover:border-electric/50 transition-colors duration-300 h-full flex flex-col"
                >
                  <div className="flex justify-center py-6 bg-gradient-to-b from-white/[0.02] to-transparent rounded-xl mb-6">
                    <PhoneFrame accent="#0066FF" className="scale-[0.85]">
                      <Screen />
                    </PhoneFrame>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-wider text-laser">
                    {project.category}
                  </span>
                  <h3 className="font-display text-xl font-semibold text-white mt-2 mb-3">
                    {project.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed flex-1">
                    {project.description}
                  </p>
                  <button
                    onClick={() => setActive(project)}
                    data-testid={`project-learn-more-${project.id}`}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-laser transition-colors duration-200 self-start"
                  >
                    Learn More <ArrowUpRight size={15} />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="bg-card-dark border border-border-subtle text-white sm:max-w-lg"
          data-testid="project-detail-modal"
        >
          {active && (
            <>
              <DialogHeader>
                <span className="font-mono text-[11px] uppercase tracking-wider text-laser">
                  {active.category}
                </span>
                <DialogTitle className="font-display text-2xl font-bold text-white mt-1">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  {active.description}
                </DialogDescription>
              </DialogHeader>
              <p className="text-slate-400 text-sm leading-relaxed mt-2">{active.description}</p>
              <div className="mt-4 space-y-2.5">
                {active.highlights.map((h) => (
                  <div key={h} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                    {h}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
